'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import type { BrandingConfig } from '../types';

/**
 * Applies a tenant's tint by overriding the `--primary*` design tokens on a
 * wrapping element. Everything inside — buttons, badges, the speaking glow,
 * LiveKit's accent — inherits the tenant color for free, because the whole
 * system reads from these variables. This is the mechanism behind §1's
 * "tenant-tinted everywhere" rule.
 */
export function BrandingScope({
  branding,
  className,
  children,
  ...props
}: {
  branding: BrandingConfig;
  className?: string;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>) {
  const style = React.useMemo<React.CSSProperties>(() => {
    if (!branding.tint) return {};
    return {
      ['--primary' as string]: branding.tint.primary,
      ['--primary-hover' as string]: branding.tint.primaryHover,
      ['--primary-active' as string]: branding.tint.primaryActive,
      ['--primary-soft' as string]: branding.tint.primarySoft,
      ['--ring' as string]: branding.tint.primary,
    };
  }, [branding.tint]);

  return (
    <div data-tenant={branding.tenantId} style={style} className={cn(className)} {...props}>
      {children}
    </div>
  );
}
